"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isTenantNotFoundError, parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import { PatientCreate } from "@/types/patient";
import { CandidatesResponse, PatientClaimAuthMode } from "@/types/patient-claim";
import { SelfSchedulePatient } from "@/types/self-schedule";
import { patientClaimQueryKey, primePatientClaim } from "./use-patient-claim";
import { PatientAuthResponse, PhoneCodeExpiredError, PortalLoginRequiredError, claimPatient, createAndClaimPatient, listPatientCandidates, signInToPortalAccountWithPhone } from "@/api/auth/patient-claim";
import { authService } from "@/services/auth-service";
import { useRecentPatientClaimStore } from "@/stores/recent-patient-claim-store";

/**
 * Send the verification SMS via Firebase Phone Auth.
 */
export function useSendMfaCode() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onSuccess: _temp2,
      onError: _temp3
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}

/**
 * Confirm the SMS code and fetch the list of patient candidates. The
 * response is *not* an authentication — it only enumerates Patients
 * matching the verified phone. The caller still needs to claim one via
 * `useClaimPatient` before patient-tagged routes work.
 */
async function _temp3(error) {
  const description = await parseError(error, "We couldn't send a verification code. Please try again.");
  toast({
    title: "Couldn't send code",
    description,
    variant: "error"
  });
}
function _temp2() {
  toast({
    title: "Code sent",
    description: "A verification code has been sent to your phone.",
    variant: "success"
  });
}
function _temp(phoneNumber) {
  return authService.signInWithPhone(phoneNumber);
}
export function useVerifyMfaCode() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp4,
      onSuccess: data => {
        const uid = authService.getCurrentUser()?.uid ?? null;
        queryClient.setQueryData(patientClaimQueryKey(uid), data ?? null);
      },
      onError: _temp5
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/**
 * Claim a candidate Patient as this Firebase user's identity. Stamps
 * `firebase_uid` on that Patient row, writes the singular `patient_id`
 * claim, refreshes the Firebase ID token, and updates the local cache so
 * `usePatientClaim` returns it as `claimedPatient`.
 */
async function _temp5(error) {
  if (await isTenantNotFoundError(error)) {
    return;
  }
  const description = await parseError(error, "We couldn't verify your phone. Please try again.");
  toast({
    title: "Verification failed",
    description,
    variant: "error"
  });
}
async function _temp4(code) {
  await authService.confirmPhoneCode(code);
  return listPatientCandidates();
}
export function useClaimPatient() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp6,
      onSuccess: claimed => {
        if (!claimed) {
          return;
        }
        const uid = authService.getCurrentUser()?.uid ?? null;
        queryClient.setQueryData(patientClaimQueryKey(uid), prev => {
          const prior = prev?.authMode === PatientClaimAuthMode.PHONE ? prev.candidates : [];
          const merged = prior.some(p_0 => p_0.id === claimed.id) ? prior.map(p => p.id === claimed.id ? claimed : p) : [...prior, claimed];
          return {
            authMode: PatientClaimAuthMode.PHONE,
            candidates: merged,
            claimedPatientId: claimed.id
          };
        });
        useRecentPatientClaimStore.getState().setRecentPatientId(claimed.id);
      },
      onError: _temp7
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/**
 * Create a new Patient under the verified phone and claim it in one shot.
 * Same post-conditions as `useClaimPatient`: claim is set, token is
 * refreshed, cache is updated.
 */
async function _temp7(error) {
  const description = await parseError(error, "We couldn't link this patient to your account. Please try again.");
  toast({
    title: "Couldn't link patient",
    description,
    variant: "error"
  });
}
function _temp6(patientId) {
  return claimPatient(patientId);
}
export function useCreateAndClaimPatient() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp8,
      onSuccess: created => {
        if (!created) {
          return;
        }
        const uid = authService.getCurrentUser()?.uid ?? null;
        queryClient.setQueryData(patientClaimQueryKey(uid), () => ({
          authMode: PatientClaimAuthMode.PHONE,
          candidates: [created],
          claimedPatientId: created.id
        }));
        useRecentPatientClaimStore.getState().setRecentPatientId(created.id);
      },
      onError: _temp9
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/**
 * Sign a phone-verified patient into the portal account they already
 * created. The phone-OTP session can't claim a Patient bound to a portal
 * account, so the server hands back a token for that account and the session
 * becomes the portal user. Seeds the claim cache for the new UID so
 * `usePatientClaim` returns the patient without a refetch race.
 */
async function _temp9(error) {
  const description = await parseError(error, "We couldn't create a new patient. Please try again.");
  toast({
    title: "Couldn't create patient",
    description,
    variant: "error"
  });
}
function _temp8(patient) {
  return createAndClaimPatient(patient);
}
export function useSignInToPortalAccountWithPhone() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp10,
      onSuccess: async patient => {
        const uid = authService.getCurrentUser()?.uid;
        if (uid) {
          await primePatientClaim(queryClient, uid, patient);
        }
      },
      onError: _temp11
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
async function _temp11(error) {
  if (error instanceof PortalLoginRequiredError || error instanceof PhoneCodeExpiredError) {
    return;
  }
  const description = await parseError(error, "We couldn't sign you in. Please try again.");
  toast({
    title: "Couldn't sign in",
    description,
    variant: "error"
  });
}
function _temp10(patientId) {
  return signInToPortalAccountWithPhone(patientId);
}